import React from 'react';
import styled from 'styled-components';

const StyledStepper = styled.div`
  display: flex;
  align-items: flex-start;
`;

const StyledStepColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const StyledStepLine = styled.div`
  flex: 1;
  height: 1px;
  margin: 0 15px;
  margin-top: 18px;
  background-color: var(--gray-border-lighter);
`;

const StyledStepCircle = styled.div<{
  $active: boolean;
  $completed: boolean;
}>`
  width: 37px;
  height: 37px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-weight: 500;
  font-size: 13px;
  line-height: 19.5px;
  cursor: pointer;
  transition: all 0.2s ease;

  ${({ $active, $completed }) => {
    if ($active || $completed) {
      return `
        background-color: var(--red-dark);
        color: var(--white-main);
        border: 1px solid var(--red-dark);
      `;
    }
    return `
      background-color: transparent;
      color: var(--black-main);
      border: 1px solid var(--gray-border-lighter);
    `;
  }}
`;

const StyledStepLabel = styled.span<{ $active: boolean }>`
  font-size: var(--xsmall-font-size);
  line-height: var(--xsmall-line-height);
  font-weight: 500;
  color: var(--black-main);
  text-align: center;
  cursor: pointer;

  @media screen and (max-width: 480px) {
    font-size: var(--xxsmall-font-size);
    line-height: var(--xxsmall-line-height);
  }
`;

const CheckmarkIcon: React.FC = () => (
  <svg
    width="15"
    height="11"
    viewBox="0 0 15 11"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M1 5L5.5 9.5L14 1"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

type SimulationDetailStepperProps = {
  stepLabels: string[];
  activeStep: number;
  onStepClick: (index: number) => void;
};

export const SimulationDetailStepper: React.FC<
  SimulationDetailStepperProps
> = ({ stepLabels, activeStep, onStepClick }) => {
  return (
    <StyledStepper>
      {stepLabels.map((label, index) => (
        <React.Fragment key={index}>
          {index > 0 && <StyledStepLine />}
          <StyledStepColumn>
            <StyledStepCircle
              $active={index === activeStep}
              $completed={index < activeStep}
              onClick={() => onStepClick(index)}
            >
              {index < activeStep ? <CheckmarkIcon /> : index + 1}
            </StyledStepCircle>
            <StyledStepLabel
              $active={index === activeStep}
              onClick={() => onStepClick(index)}
            >
              {label}
            </StyledStepLabel>
          </StyledStepColumn>
        </React.Fragment>
      ))}
    </StyledStepper>
  );
};
